import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, User, MapPin, Sparkles, UserPlus, LogOut, Shield } from 'lucide-react';
import NotificationBell from './notifications/NotificationBell';
import vybetribeLogo from '../assets/vybetribe-logo.png';
import LocationPrompt from './LocationPrompt';
import VybeCounterModal from './VybeCounterModal';
import SubscriptionModal from './SubscriptionModal';
import InviteModal from './InviteModal';
import { checkVybeLimits, VybeLimits } from '../utils/vybeLimits';

interface NavbarProps {
  toggleSidebar: () => void;
  onStartVybe?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ toggleSidebar, onStartVybe }) => {
  const { currentUser, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [showVybeModal, setShowVybeModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [vybeLimits, setVybeLimits] = useState<VybeLimits | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const hasLocation = currentUser?.latitude && currentUser?.longitude;
  const isSubscribed =
    currentUser?.subscriptionTier === 'subscribed' ||
    currentUser?.subscriptionTier === 'subscribed';

  useEffect(() => {
    const fetchLimits = async () => {
      if (!currentUser) return;
      const limits = await checkVybeLimits(currentUser.id, isSubscribed);
      setVybeLimits(limits);
    };
    fetchLimits();
  }, [currentUser, isSubscribed, showVybeModal]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  const handleLogout = async () => {
    try {
      await logout();
      setShowProfileMenu(false);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-[45] bg-theme-background/95 backdrop-blur-md border-b border-theme-cards text-white"
      style={{ overflow: 'visible' }}
    >
      <div className="flex items-center justify-between px-4 h-14" style={{ overflow: 'visible' }}>
        {/* LEFT: logo (and optional desktop menu) */}
        <div className="flex items-center gap-3">
          {/* Desktop Sidebar Button */}
          <button
            onClick={toggleSidebar}
            className="hidden md:flex w-9 h-9 rounded-full hover:bg-theme-cards transition-all focus:outline-none focus:ring-2 focus:ring-theme-primary items-center justify-center flex-shrink-0"
            aria-label="Toggle menu"
            type="button"
          >
            <Menu size={18} className="text-theme-text-main" />
          </button>

          <Link to="/" className="flex items-center hover:opacity-80 transition-all flex-shrink-0">
            <img src={vybetribeLogo} alt="VybeTribe" className="h-8 w-auto object-contain" />
          </Link>
        </div>

        {/* RIGHT */}
        <div className="flex items-center gap-3 flex-shrink-0" style={{ overflow: 'visible' }}>
          {isAuthenticated ? (
            <>
              {/* Location Button - Desktop Only */}
              <button
                onClick={() => setShowLocationPrompt(!showLocationPrompt)}
                className={`hidden md:flex items-center justify-center py-2 focus:outline-none ${hasLocation ? 'active' : ''}`}
                aria-label={hasLocation ? 'Location enabled' : 'Enable location'}
              >
                <MapPin className="w-6 h-6" />
              </button>

              <LocationPrompt isOpen={showLocationPrompt} onClose={() => setShowLocationPrompt(false)} />

              {/* Invite Button - Desktop only */}
              <button
                onClick={() => setShowInviteModal(true)}
                className="hidden md:flex items-center justify-center py-2"
                aria-label="Invite friends"
              >
                <UserPlus className="w-6 h-6" />
              </button>

              <InviteModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} />

              {/* Vybe Counter */}
              <div className="relative flex items-center">
                <button
                  onClick={() => setShowVybeModal(!showVybeModal)}
                  className="h-8 flex items-center bg-gradient-to-r from-purple-600 to-pink-600 px-3 rounded-full hover:from-purple-700 hover:to-pink-700 transition-all cursor-pointer focus:outline-none flex-shrink-0"
                  title="View Vybe Tokens"
                >
                  <Sparkles size={12} className="text-white flex-shrink-0" />
                  <span className="text-white text-[11px] font-semibold ml-1 whitespace-nowrap">
                    {vybeLimits
                      ? `${vybeLimits.create.remaining}/${vybeLimits.create.monthly + vybeLimits.create.tokens}`
                      : '...'}
                  </span>
                </button>

                <VybeCounterModal
                  isOpen={showVybeModal}
                  onClose={() => setShowVybeModal(false)}
                  onUpgrade={() => {
                    setShowVybeModal(false);
                    setShowSubscriptionModal(true);
                  }}
                  onStartVybe={() => {
                    setShowVybeModal(false);
                    onStartVybe ? onStartVybe() : navigate('/my-events');
                  }}
                />

                <SubscriptionModal
                  isOpen={showSubscriptionModal}
                  onClose={() => setShowSubscriptionModal(false)}
                  requiredFeature="Premium Membership"
                />
              </div>

              {/* Notifications */}
              <NotificationBell />

              {/* Avatar */}
              <div className="relative flex items-center" ref={profileMenuRef}>
                <button
                  onClick={() => {
                    if (window.innerWidth >= 768) {
                      navigate('/myAccount');
                    } else {
                      setShowProfileMenu(!showProfileMenu);
                    }
                  }}
                  className="relative flex items-center flex-shrink-0"
                  title="View My Profile"
                >
                  {currentUser?.avatarUrl ? (
                    <div className="w-8 h-8 rounded-full overflow-hidden">
                      <img src={currentUser.avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <User className="w-8 h-8" />
                  )}

                  {!hasLocation && (
                    <span className="md:hidden absolute top-1 right-0 inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
                  )}
                </button>

                {/* Mobile Dropdown */}
                {showProfileMenu && (
                  <div className="md:hidden absolute right-0 top-full mt-2 w-56 bg-theme-cards rounded-lg shadow-xl border border-theme-cards overflow-hidden z-50">
                    <button
                      onClick={() => {
                        setShowLocationPrompt(true);
                        setShowProfileMenu(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-theme-text-main hover:bg-theme-background ${
                        !hasLocation ? 'bg-amber-500/10' : ''
                      }`}
                    >
                      <MapPin size={18} className={!hasLocation ? 'text-amber-500' : ''} />
                      <div className="flex-1">
                        <span>{hasLocation ? 'Location Set' : 'Set Location'}</span>
                        {!hasLocation && <div className="text-xs text-amber-500 mt-0.5">See nearby Vybes</div>}
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        setShowInviteModal(true);
                        setShowProfileMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-theme-text-main hover:bg-theme-background border-t border-theme-cards"
                    >
                      <UserPlus size={18} />
                      <span>Invite Friends</span>
                    </button>

                    <button
                      onClick={() => {
                        navigate('/myAccount');
                        setShowProfileMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-theme-text-main hover:bg-theme-background border-t border-theme-cards"
                    >
                      <User size={18} />
                      <span>My Profile</span>
                    </button>

                    {currentUser?.isAdmin && (
                      <button
                        onClick={() => {
                          navigate('/admin');
                          setShowProfileMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-theme-text-main hover:bg-theme-background border-t border-theme-cards"
                      >
                        <Shield size={18} />
                        <span>Admin Console</span>
                      </button>
                    )}

                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-red-500 hover:bg-theme-background border-t border-theme-cards"
                    >
                      <LogOut size={18} />
                      <span>Log Out</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            // Logged out CTAs - subtle and horizontal
            <div className="flex items-center gap-3 md:gap-4">
              <button
                onClick={() => navigate('/login?signup=true')}
                className="text-white/90 hover:text-white hover:underline transition-all text-sm font-normal whitespace-nowrap"
              >
                Join free
              </button>

              <button
                onClick={() => navigate('/login')}
                className="text-white/90 hover:text-white hover:underline transition-all text-sm font-normal whitespace-nowrap"
              >
                Log in
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
